import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MarketplaceDetail } from '@anytag/graphql';
import { formatIntNumber } from '@anytag/shared-utils';
import { useAllTalentAgencyOrPartnerOptions } from '@anytag/shared-hooks';
import { SelectorField, FILTER_SECTIONS, Filter, RangeSliderInputField } from '@anytag/shared-molecules';
import { useMarketplaceInfluencersFilterOptions } from './hooks';
import { defaultMarketplaceInfluencersFilterValues } from './utils';
import { MarketplaceInfluencersFilterFormKeys, MarketplaceInfluencersFilterFormValues } from './schemaTypes';

interface AdvancedFilterProps {
  count: number;
  onSubmit?: () => void;
  marketplace: MarketplaceDetail;
}

export const AdvancedFilter = ({ count, onSubmit, marketplace }: AdvancedFilterProps) => {
  const { t } = useTranslation();
  const { allTalentAgencyPartnerOptions } = useAllTalentAgencyOrPartnerOptions();
  const { watch, reset } = useFormContext<MarketplaceInfluencersFilterFormValues>();
  const { ageConfigs, genderOptions, influencerCategoryOptions, followerConfigs, engagementRateConfigs } =
    useMarketplaceInfluencersFilterOptions({ marketplace });

  const values = watch();

  const handleClear = () => {
    reset({
      ...defaultMarketplaceInfluencersFilterValues,
      // Basic filter
      keyword: values.keyword,
      participateStatuses: values.participateStatuses
    });
  };

  return (
    <Filter.MoreFilterPopover
      count={count}
      onSubmit={onSubmit}
      onClear={handleClear}
      items={[
        {
          value: FILTER_SECTIONS.InfluencerAttributes,
          title: FILTER_SECTIONS.InfluencerAttributes,
          node: (
            <>
              <SelectorField<MarketplaceInfluencersFilterFormKeys>
                name="genders"
                options={genderOptions}
                title={t('Selector.Gender')}
                multiple
              />
              <SelectorField<MarketplaceInfluencersFilterFormKeys>
                name="categoryIds"
                title={t('Selector.Categories')}
                options={influencerCategoryOptions}
                multiple
              />
              <SelectorField<MarketplaceInfluencersFilterFormKeys>
                name="talentAgencyOrPartnerIds"
                options={allTalentAgencyPartnerOptions}
                title={t('Selector.Partner & Talent Agency')}
                multiple
              />
            </>
          )
        },
        {
          value: FILTER_SECTIONS.PerformanceFilter,
          title: FILTER_SECTIONS.PerformanceFilter,
          node: (
            <>
              <RangeSliderInputField<MarketplaceInfluencersFilterFormKeys>
                title={t('General.Age')}
                options={ageConfigs.options}
                slideProps={ageConfigs.slideProps}
                minField={{ name: 'minAge', placeholder: `${ageConfigs.slideProps.min}` }}
                maxField={{ name: 'maxAge', placeholder: `${ageConfigs.slideProps.max}` }}
              />
              <RangeSliderInputField<MarketplaceInfluencersFilterFormKeys>
                options={followerConfigs.options}
                slideProps={followerConfigs.slideProps}
                title={t('General.Number of Followers')}
                minField={{ name: 'minFollowers', placeholder: formatIntNumber(followerConfigs.slideProps.min) }}
                maxField={{ name: 'maxFollowers', placeholder: `${formatIntNumber(followerConfigs.slideProps.max)}+` }}
              />
              <RangeSliderInputField<MarketplaceInfluencersFilterFormKeys>
                title={t('General.Engagement Rate')}
                options={engagementRateConfigs.options}
                slideProps={engagementRateConfigs.slideProps}
                minField={{ name: 'minEngagementRate', placeholder: `${engagementRateConfigs.slideProps.min}%` }}
                maxField={{ name: 'maxEngagementRate', placeholder: `${engagementRateConfigs.slideProps.max}%` }}
              />
            </>
          )
        }
      ]}
    />
  );
};
